import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Package, AlertTriangle } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreatorBillingPageWorking = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/creator/billing`);
        setData(res.data);
      } catch (err) {
        console.error('Failed to load billing:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) return <div style={{ padding: '40px', color: theme.colors.text.muted }}>Loading billing...</div>;

  const d = data || {};
  const usedPct = d.base_contracts ? Math.round((d.contracts_used / d.base_contracts) * 100) : 0;

  return (
    <div data-testid="creator-billing-page" style={{ padding: '40px', maxWidth: '800px' }}>
      <h1 style={{ fontSize: '28px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '8px' }}>Billing & Subscription</h1>
      <p style={{ color: theme.colors.text.secondary, fontSize: '15px', marginBottom: '32px' }}>Manage your plan and contract quota</p>

      {d.message && (
        <div data-testid="billing-message" style={{
          padding: '16px 20px', marginBottom: '24px', borderRadius: '12px',
          background: `${theme.colors.status.warning}10`, border: `1px solid ${theme.colors.status.warning}30`,
          color: theme.colors.status.warning, fontSize: '14px',
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <AlertTriangle size={18} /> {d.message}
        </div>
      )}

      {/* Plan Card */}
      <div style={{ background: theme.colors.bg.card, borderRadius: theme.radius.card, padding: '24px', border: `1px solid ${theme.colors.borders}`, marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <FileText size={20} color={theme.colors.primary} />
          <h3 style={{ color: theme.colors.text.primary, fontSize: '18px', fontWeight: 600 }}>Current Plan</h3>
        </div>
        <div style={{
          display: 'inline-block', padding: '6px 16px', borderRadius: '20px', fontSize: '14px', fontWeight: 700,
          background: `${theme.colors.primary}20`, color: theme.colors.primary, marginBottom: '16px',
        }}>
          {d.plan || 'Free'} Plan
        </div>
        <p style={{ color: theme.colors.text.secondary, fontSize: '14px' }}>
          Includes {d.base_contracts || 10} portfolio contracts
        </p>
      </div>

      {/* Quota Card */}
      <div style={{ background: theme.colors.bg.card, borderRadius: theme.radius.card, padding: '24px', border: `1px solid ${theme.colors.borders}`, marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <Package size={20} color={theme.colors.primary} />
          <h3 style={{ color: theme.colors.text.primary, fontSize: '18px', fontWeight: 600 }}>Contract Quota</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
          <div data-testid="billing-used"
            onClick={() => navigate('/creator/contracts?filter=used')}
            style={{ textAlign: 'center', cursor: 'pointer', padding: '12px', borderRadius: theme.radius.sm, transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = theme.colors.bg.secondary}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            title="View used contracts">
            <p style={{ color: theme.colors.text.muted, fontSize: '13px', marginBottom: '4px' }}>Used</p>
            <p style={{ color: theme.colors.primary, fontSize: '32px', fontWeight: 700 }}>{d.contracts_used}</p>
            <p style={{ color: theme.colors.primary, fontSize: '11px', fontWeight: 600, marginTop: '2px' }}>View list →</p>
          </div>
          <div data-testid="billing-remaining"
            onClick={() => navigate('/creator/contracts?filter=remaining')}
            style={{ textAlign: 'center', cursor: 'pointer', padding: '12px', borderRadius: theme.radius.sm, transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = theme.colors.bg.secondary}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            title="Create a new contract">
            <p style={{ color: theme.colors.text.muted, fontSize: '13px', marginBottom: '4px' }}>Remaining</p>
            <p style={{ color: theme.colors.status.success, fontSize: '32px', fontWeight: 700 }}>{d.contracts_remaining}</p>
            <p style={{ color: theme.colors.status.success, fontSize: '11px', fontWeight: 600, marginTop: '2px' }}>Create new →</p>
          </div>
          <div data-testid="billing-total" style={{ textAlign: 'center', padding: '12px' }}>
            <p style={{ color: theme.colors.text.muted, fontSize: '13px', marginBottom: '4px' }}>Total</p>
            <p style={{ color: theme.colors.text.primary, fontSize: '32px', fontWeight: 700 }}>{d.base_contracts}</p>
          </div>
        </div>
        {/* Progress bar */}
        <div style={{ height: '8px', borderRadius: '4px', background: theme.colors.bg.primary, overflow: 'hidden' }}>
          <div style={{
            width: `${usedPct}%`, height: '100%', borderRadius: '4px',
            background: usedPct >= 90 ? theme.colors.status.error : usedPct >= 70 ? theme.colors.status.warning : theme.colors.primary,
            transition: 'width 0.5s ease',
          }} />
        </div>
        <p style={{ color: theme.colors.text.muted, fontSize: '12px', marginTop: '8px' }}>{usedPct}% of quota used</p>
      </div>
    </div>
  );
};

export default CreatorBillingPageWorking;
